import { useEffect, useState } from "react";
import type { CommentItem, Discussion } from "./types";
import { toneBadge, toneSolid } from "./tones";

const POOL: CommentItem[] = [
  { name: "熬夜冲浪选手", tone: "accent", initial: "浪", time: "8 分钟前", likes: 1280, badge: "楼主", text: "先马克再看！这个话题我蹲了一整天，终于有人开帖了，前排小板凳已就位。" },
  { name: "理性吃瓜人", tone: "primary", initial: "理", time: "15 分钟前", likes: 866, text: "补充一个背景：这事昨天就有苗头了，只是今天才被大规模讨论。先观望，别急着下结论。" },
  { name: "评论区一姐", tone: "secondary", initial: "评", time: "26 分钟前", likes: 642, text: "看到大家都在吵，我也忍不住说两句。其实两边都有道理，就看后续怎么回应了。" },
  { name: "站队墙头草", tone: "accent", initial: "墙", time: "40 分钟前", likes: 391, text: "刚爬完楼，信息量太大了。有人整理个时间线吗？求个省流版。" },
  { name: "前排吃瓜", tone: "primary", initial: "前", time: "1 小时前", likes: 288, text: "蹲到半夜果然有惊喜，评论区果然没让我失望，个个都是人才。" },
];

export default function DiscussionDrawer({
  topic,
  onClose,
}: {
  topic: Discussion | null;
  onClose: () => void;
}) {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [text, setText] = useState("");

  useEffect(() => {
    if (topic) {
      setComments(POOL.map((c, i) => ({ ...c, time: `${(i + 1) * 6 + 8} 分钟前` })));
      setText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic?.id]);

  if (!topic) return null;

  const addComment = () => {
    const value = text.trim();
    if (!value) return;
    setComments((prev) => [
      { name: "我（现场吃瓜）", tone: "secondary", initial: "我", time: "刚刚", likes: 0, text: value },
      ...prev,
    ]);
    setText("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="关闭详情"
      ></button>

      <div className="anim-pop hot-scroll relative z-10 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-background-50 sm:rounded-3xl">
        {/* 头部 */}
        <div className="flex items-center justify-between border-b border-background-200 bg-background-50 px-5 py-3.5 md:px-6">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-chat-3-fill text-primary-500" aria-hidden="true"></i>
            话题详情 · 评论区
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </div>

        {/* 滚动内容 */}
        <div className="hot-scroll flex-1 overflow-y-auto">
          <div className="px-5 py-5 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${toneBadge[topic.tagTone]}`}>{topic.categoryName}</span>
              <span className="rounded-md bg-primary-500 px-1.5 py-0.5 text-[11px] font-bold text-white">{topic.tag}</span>
            </div>
            <h3 className="mt-3 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">{topic.title}</h3>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white ${toneSolid[topic.author.tone]}`}>
                  {topic.author.initial}
                </span>
                <span className="font-medium text-foreground-800">{topic.author.name} 发起</span>
              </span>
              <span>{topic.time}</span>
              <span>· 围观 {topic.views}</span>
            </div>

            <p className="mt-4 rounded-2xl bg-background-100 p-4 text-[15px] leading-relaxed text-foreground-700">
              {topic.summary}
            </p>

            {/* 统计 */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-semibold text-primary-700">
                <i className="ri-thumb-up-line" aria-hidden="true"></i>
                点赞 {topic.likes}
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
                <i className="ri-eye-line" aria-hidden="true"></i>
                围观 {topic.views}
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-semibold text-accent-800">
                <i className="ri-fire-fill" aria-hidden="true"></i>
                热度 {topic.heat}
              </span>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <h4 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-chat-4-line text-primary-500" aria-hidden="true"></i>
                网友热议
                <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-medium text-foreground-600">{comments.length}</span>
              </h4>
              <span className="text-[11px] text-foreground-400">文明发言，友善围观</span>
            </div>

            {/* 评论列表 */}
            <ul className="mt-3 space-y-3">
              {comments.map((c, i) => (
                <li key={i} className="rounded-2xl border border-background-200 bg-background-50 p-3.5">
                  <div className="flex items-start gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${toneSolid[c.tone]}`}>
                      {c.initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-semibold text-foreground-900">{c.name}</span>
                        {c.badge && (
                          <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-semibold text-accent-800">{c.badge}</span>
                        )}
                        <span className="text-foreground-400">{c.time}</span>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{c.text}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-foreground-400">
                        <span className="flex items-center gap-1 cursor-pointer hover:text-primary-500">
                          <i className="ri-thumb-up-line" aria-hidden="true"></i>
                          {c.likes > 0 ? c.likes : "赞"}
                        </span>
                        <span className="flex items-center gap-1 cursor-pointer hover:text-primary-500">
                          <i className="ri-chat-1-line" aria-hidden="true"></i>回复
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 输入区 */}
        <div className="border-t border-background-200 bg-background-100/70 px-4 py-3 md:px-6">
          <div className="flex items-end gap-3">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={1}
              placeholder="我也来说两句…（文明吃瓜）"
              maxLength={500}
              className="max-h-32 min-h-[44px] flex-1 resize-none rounded-2xl border border-background-200 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
            ></textarea>
            <button
              type="button"
              onClick={addComment}
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              发布评论
            </button>
          </div>
          <p className="mt-1.5 px-1 text-[11px] text-foreground-400">你的评论将实时展示在话题讨论区</p>
        </div>
      </div>
    </div>
  );
}